import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import toast from 'react-hot-toast';
import { PlusIcon, UserGroupIcon, ClockIcon, BookOpenIcon, CalendarDaysIcon, CheckCircleIcon, XCircleIcon, MagnifyingGlassIcon, SparklesIcon } from '@heroicons/react/24/solid';
import axios from 'axios';
import moment from 'moment';

// Motivational quotes array
const motivationalQuotes = [
  "Education is the most powerful weapon which you can use to change the world. - Nelson Mandela",
  "The beautiful thing about learning is that nobody can take it away from you. - B.B. King",
  "Live as if you were to die tomorrow. Learn as if you were to live forever. - Mahatma Gandhi",
  "The expert in anything was once a beginner. - Helen Hayes",
  "Learning is not attained by chance, it must be sought for with ardor. - Abigail Adams",
  "Education is not preparation for life; education is life itself. - John Dewey",
  "The capacity to learn is a gift; the ability to learn is a skill; the willingness to learn is a choice. - Brian Herbert",
  "Success is the sum of small efforts repeated day in and day out. - Robert Collier",
  "Don't let what you cannot do interfere with what you can do. - John Wooden",
  "Strive for progress, not perfection. - Unknown",
  "The only way to learn mathematics is to do mathematics. - Paul Halmos",
  "Tell me and I forget. Teach me and I remember. Involve me and I learn. - Benjamin Franklin"
];

const Dashboard = () => {
  const [classrooms, setClassrooms] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [completedSessions, setCompletedSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [completedLoading, setCompletedLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTooEarlyModal, setShowTooEarlyModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [newClassroom, setNewClassroom] = useState({ name: '', description: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [dailyQuote] = useState(() => {
    // Select a random quote that stays consistent for the session
    return motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)];
  });

  // Admin tutor filter state
  const [allTutors, setAllTutors] = useState([]);
  const [selectedTutorId, setSelectedTutorId] = useState('');
  const [selectedTutorClassrooms, setSelectedTutorClassrooms] = useState([]);
  const [selectedTutorSessions, setSelectedTutorSessions] = useState(null);
  const [tutorDataLoading, setTutorDataLoading] = useState(false);
  const [confirmedAfter, setConfirmedAfter] = useState('');
  const [confirmedBefore, setConfirmedBefore] = useState('');

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchClassrooms();
    fetchUpcomingSessions();
    fetchCompletedSessions();
    // Fetch tutors list for admin
    if (user?.user_metadata?.role === 'admin') {
      fetchAllTutors();
    }
  }, []);

  // Fetch tutor data when selection or date filters change
  useEffect(() => {
    if (selectedTutorId) {
      fetchTutorData(selectedTutorId, confirmedAfter, confirmedBefore);
    } else {
      setSelectedTutorClassrooms([]);
      setSelectedTutorSessions(null);
    }
  }, [selectedTutorId, confirmedAfter, confirmedBefore]);

  const fetchClassrooms = async () => {
    try {
      const response = await axios.get('/api/classrooms');
      const allClassrooms = Array.isArray(response.data) ? response.data : [];

      // Admins see all classrooms
      if (user?.user_metadata?.role === 'admin') {
        console.log(`Admin user - showing all ${allClassrooms.length} classrooms`);
        setClassrooms(allClassrooms);
        setLoading(false);
        return;
      }

      // Filter classrooms to only show owned or enrolled ones
      const filteredClassrooms = [];

      for (const classroom of allClassrooms) {
        // If user is the tutor, include it
        if (classroom.tutor_id === user.id) {
          filteredClassrooms.push(classroom);
          continue;
        }

        // Check if user is enrolled
        try {
          const enrollmentResponse = await axios.get(`/api/classrooms/${classroom.id}/students`);
          const isEnrolled = enrollmentResponse.data.some(
            enrollment => enrollment.student_id === user.id
          );
          if (isEnrolled) {
            filteredClassrooms.push(classroom);
          }
        } catch (error) {
          console.error(`Error checking enrollment for classroom ${classroom.id}:`, error);
        }
      }

      setClassrooms(filteredClassrooms);
    } catch (error) {
      toast.error('Failed to load classrooms');
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingSessions = async () => {
    try {
      setSessionsLoading(true);
      const response = await axios.get(`/api/sessions/user/${user.id}`);
      setUpcomingSessions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load upcoming sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  };

  const fetchCompletedSessions = async () => {
    try {
      setCompletedLoading(true);
      const response = await axios.get(`/api/sessions/completed/${user.id}`);
      setCompletedSessions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load completed sessions:', error);
    } finally {
      setCompletedLoading(false);
    }
  };

  // Admin: Fetch all tutors
  const fetchAllTutors = async () => {
    try {
      const response = await axios.get('/api/admin/tutors');
      setAllTutors(response.data.tutors || []);
    } catch (error) {
      console.error('Failed to load tutors:', error);
    }
  };

  // Admin: Fetch data for selected tutor
  const fetchTutorData = async (tutorId, afterDate = '', beforeDate = '') => {
    setTutorDataLoading(true);
    try {
      // Build query params for date filter
      const params = new URLSearchParams();
      if (afterDate) params.append('confirmedAfter', afterDate);
      if (beforeDate) params.append('confirmedBefore', beforeDate);
      const queryString = params.toString() ? `?${params.toString()}` : '';

      const [classroomsRes, sessionsRes] = await Promise.all([
        axios.get(`/api/admin/tutors/${tutorId}/classrooms`),
        axios.get(`/api/admin/tutors/${tutorId}/completed-sessions${queryString}`)
      ]);
      setSelectedTutorClassrooms(classroomsRes.data.classrooms || []);
      setSelectedTutorSessions(sessionsRes.data);
    } catch (error) {
      console.error('Failed to load tutor data:', error);
      toast.error('Failed to load tutor data');
    } finally {
      setTutorDataLoading(false);
    }
  };

  const handleConfirmSession = async (sessionId) => {
    try {
      const userRole = user?.user_metadata?.role === 'tutor' ? 'tutor' : 'student';
      await axios.post(`/api/sessions/${sessionId}/confirm`, {
        userId: user.id,
        role: userRole
      });
      toast.success('Session confirmed successfully!');
      // Refresh completed sessions
      fetchCompletedSessions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to confirm session');
    }
  };

  const createClassroom = async (e) => {
    e.preventDefault();
    
    if (!newClassroom.name || !newClassroom.description) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const response = await axios.post('/api/classrooms', {
        ...newClassroom,
        tutor_id: user.id
      });
      
      setClassrooms([response.data, ...classrooms]);
      setShowCreateModal(false);
      setNewClassroom({ name: '', description: '' });
      toast.success('Classroom created successfully!');
    } catch (error) {
      toast.error('Failed to create classroom');
    }
  };

  const joinClassroom = (classroomId) => {
    navigate(`/classroom/${classroomId}`);
  };

  const handleSessionClick = (session) => {
    const now = moment();
    const sessionStart = moment(session.start_time);
    const minutesUntilStart = sessionStart.diff(now, 'minutes');

    // Allow joining if within 5 minutes before start time or if session has already started
    if (minutesUntilStart <= 5) {
      navigate(`/classroom/${session.classroom_id}`);
    } else {
      setSelectedSession(session);
      setShowTooEarlyModal(true);
    }
  };

  // Calculate total hours tutored from completed sessions
  // Only counts sessions that are fully confirmed by both tutor and students
  const calculateTotalHours = () => {
    const userRole = user?.user_metadata?.role;
    if (userRole !== 'tutor' && userRole !== 'admin') return 0;
    if (!Array.isArray(completedSessions)) return 0;

    return completedSessions.reduce((total, session) => {
      // Only count sessions that have been confirmed by both tutor and students
      if (session.completion_status === 'confirmed' && session.start_time && session.end_time) {
        const start = moment(session.start_time);
        const end = moment(session.end_time);
        const duration = moment.duration(end.diff(start));
        const hours = duration.asHours();
        return total + hours;
      }
      return total;
    }, 0);
  };

  const totalHoursTutored = calculateTotalHours();

  // Filter classrooms based on search query
  const filteredClassrooms = (Array.isArray(classrooms) ? classrooms : []).filter(classroom => {
    const searchLower = searchQuery.toLowerCase();
    return (
      classroom.name.toLowerCase().includes(searchLower) ||
      classroom.description.toLowerCase().includes(searchLower)
    );
  });

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900 dashboard-container">
      <div className="flex flex-col lg:flex-row h-full gap-6 p-6">
        {/* Main Content */}
        <div className="flex-1">
          {/* Welcome Banner */}
          <div className={`mb-6 bg-gradient-to-r from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-700 rounded-lg shadow-lg overflow-hidden ${user?.user_metadata?.role === 'admin' ? 'max-w-3xl' : ''}`}>
            <div className="px-6 py-5">
              <div className="flex items-center mb-2">
                <SparklesIcon className="h-6 w-6 text-white mr-2" />
                <h1 className="text-2xl font-bold text-white">
                  {getGreeting()}, {(user?.user_metadata?.name || user?.email?.split('@')[0] || 'there').split(' ')[0]}!
                </h1>
              </div>
              <p className="text-primary-100 dark:text-primary-200 italic text-sm leading-relaxed">
                "{dailyQuote}"
              </p>
            </div>
          </div>

          {/* Tutor Stats - Show for tutors only (admins use Tutor Overview section) */}
          {user?.user_metadata?.role === 'tutor' && !completedLoading && (
            <div className="mb-6">
              <div className="flex flex-wrap gap-3">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 p-4 border border-transparent dark:border-gray-700 w-72">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">Total Hours Tutored</p>
                      <p className="text-2xl font-bold text-primary-600 dark:text-primary-400 mt-0.5">
                        {totalHoursTutored.toFixed(1)}
                      </p>
                    </div>
                    <div className="bg-primary-100 dark:bg-primary-900 p-2 rounded-lg flex-shrink-0">
                      <ClockIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 p-4 border border-transparent dark:border-gray-700 w-72">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">Completed Sessions</p>
                      <p className="text-2xl font-bold mt-0.5" style={{ color: '#F4C70F' }}>
                        {completedSessions.length}
                      </p>
                    </div>
                    <div className="p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: '#FEF9E7' }}>
                      <CheckCircleIcon className="h-6 w-6" style={{ color: '#F4C70F' }} />
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 p-4 border border-transparent dark:border-gray-700 w-72">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">Active Classrooms</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-0.5">
                        {classrooms.filter(c => c.tutor_id === user.id).length}
                      </p>
                    </div>
                    <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg flex-shrink-0">
                      <BookOpenIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Admin Tutor Filter Section */}
          {user?.user_metadata?.role === 'admin' && (
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700 max-w-6xl">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Tutor Overview</h2>

              {/* Tutor Selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Tutor
                </label>
                <select
                  value={selectedTutorId}
                  onChange={(e) => setSelectedTutorId(e.target.value)}
                  className="w-full md:w-80 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">-- Select a tutor --</option>
                  {allTutors.map((tutor) => (
                    <option key={tutor.id} value={tutor.id}>
                      {tutor.name} ({tutor.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Filter for Confirmed Sessions */}
              {selectedTutorId && (
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600 dark:text-gray-400">Confirmed After:</label>
                    <input
                      type="date"
                      value={confirmedAfter}
                      onChange={(e) => setConfirmedAfter(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600 dark:text-gray-400">Confirmed Before:</label>
                    <input
                      type="date"
                      value={confirmedBefore}
                      onChange={(e) => setConfirmedBefore(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  {(confirmedAfter || confirmedBefore) && (
                    <button
                      onClick={() => {
                        setConfirmedAfter('');
                        setConfirmedBefore('');
                      }}
                      className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              )}

              {/* Loading State */}
              {tutorDataLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                </div>
              )}

              {/* Tutor Data */}
              {selectedTutorId && !tutorDataLoading && (
                <div className="space-y-6">
                  {/* Summary Stats */}
                  {selectedTutorSessions && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total Hours</p>
                        <p className="text-xl font-bold text-primary-600 dark:text-primary-400">
                          {selectedTutorSessions.totalHours.toFixed(1)}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Confirmed Sessions</p>
                        <p className="text-xl font-bold text-green-600 dark:text-green-400">
                          {selectedTutorSessions.confirmedSessions}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total Sessions</p>
                        <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {selectedTutorSessions.totalSessions}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Classrooms</p>
                        <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                          {selectedTutorClassrooms.length}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Classrooms with Students */}
                  <div>
                    <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3">
                      Classrooms & Students
                    </h3>
                    {selectedTutorClassrooms.length === 0 ? (
                      <p className="text-gray-500 dark:text-gray-400 text-sm">No classrooms found</p>
                    ) : (
                      <div className="space-y-3">
                        {selectedTutorClassrooms.map((classroom) => (
                          <div
                            key={classroom.id}
                            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium text-gray-900 dark:text-white">
                                {classroom.name}
                              </h4>
                              <button
                                onClick={() => navigate(`/classroom/${classroom.id}`)}
                                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                              >
                                Open Classroom
                              </button>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                              Code: {classroom.code}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {classroom.students.length === 0 ? (
                                <span className="text-xs text-gray-400">No students enrolled</span>
                              ) : (
                                classroom.students.map((student) => (
                                  <span
                                    key={student.id}
                                    className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full"
                                  >
                                    {student.name}
                                  </span>
                                ))
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Completed Sessions */}
                  {selectedTutorSessions && selectedTutorSessions.sessions.length > 0 && (
                    <div>
                      <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3">
                        Completed Sessions (for payment)
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-600">
                              <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">Date</th>
                              <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">Session</th>
                              <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">Classroom</th>
                              <th className="text-center py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">Hours</th>
                              <th className="text-center py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">Status</th>
                              <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">Confirmed On</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedTutorSessions.sessions.slice(0, 10).map((session) => (
                              <tr key={session.id} className="border-b border-gray-100 dark:border-gray-700">
                                <td className="py-2 px-3 text-gray-900 dark:text-white">
                                  {moment(session.start_time).format('MMM D, YYYY')}
                                </td>
                                <td className="py-2 px-3 text-gray-900 dark:text-white">
                                  {session.title}
                                </td>
                                <td className="py-2 px-3 text-gray-600 dark:text-gray-400">
                                  {session.classroom_name}
                                </td>
                                <td className="py-2 px-3 text-center text-gray-900 dark:text-white">
                                  {session.duration_hours.toFixed(1)}
                                </td>
                                <td className="py-2 px-3 text-center">
                                  {session.fully_confirmed ? (
                                    <span className="inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs rounded-full">
                                      <CheckCircleIcon className="h-3 w-3 mr-1" />
                                      Confirmed
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs rounded-full">
                                      Pending
                                    </span>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-gray-600 dark:text-gray-400">
                                  {session.fully_confirmed_at ? moment(session.fully_confirmed_at).format('MMM D, YYYY') : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {selectedTutorSessions.sessions.length > 10 && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                            Showing 10 of {selectedTutorSessions.sessions.length} sessions
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">My Classrooms</h2>
            <p className="text-gray-600 dark:text-gray-400">Join a classroom to start learning</p>
          </div>

        {/* Upcoming Sessions Widget */}
        {!sessionsLoading && upcomingSessions.length > 0 && (
          <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <CalendarDaysIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Upcoming Sessions</h2>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">{upcomingSessions.length} session{upcomingSessions.length !== 1 ? 's' : ''}</span>
            </div>

            <div className="space-y-3">
              {upcomingSessions.slice(0, 3).map((session) => {
                const classroom = classrooms.find(c => c.id === session.classroom_id);
                return (
                  <div
                    key={session.id}
                    onClick={() => handleSessionClick(session)}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-150 cursor-pointer"
                  >
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 dark:text-white">{session.title}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {classroom?.name || 'Unknown Classroom'}
                      </p>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="text-right">
                        <p className="font-medium">{moment(session.start_time).format('MMM D, YYYY')}</p>
                        <p className="text-xs">{moment(session.start_time).format('h:mm A')}</p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        moment(session.start_time).diff(moment(), 'hours') < 1
                          ? 'bg-red-100 text-red-700'
                          : moment(session.start_time).diff(moment(), 'hours') < 24
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {moment(session.start_time).diff(moment(), 'hours') < 1
                          ? 'Starting soon'
                          : moment(session.start_time).fromNow()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {upcomingSessions.length > 3 && (
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  And {upcomingSessions.length - 3} more session{upcomingSessions.length - 3 !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          {/* Create Classroom Button - Only for admins */}
          {user?.user_metadata?.role === 'admin' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 hover:shadow-md active:scale-95 transition-all duration-200"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create New Classroom
            </button>
          )}

          {/* Join Classroom Button - For all users */}
          <button
            onClick={() => navigate('/join')}
            className="inline-flex items-center px-4 py-2 text-white font-semibold rounded-lg transition-colors"
            style={{ backgroundColor: '#F4C70F' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#D4A800'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F4C70F'}
          >
            <UserGroupIcon className="h-5 w-5 mr-2" />
            Join Classroom
          </button>
        </div>

        {/* Search Bar */}
        {classrooms.length > 0 && (
          <div className={`mb-6 ${user?.user_metadata?.role === 'admin' ? 'max-w-xl' : ''}`}>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="Search classrooms by name or description..."
              />
            </div>
            {searchQuery && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Found {filteredClassrooms.length} classroom{filteredClassrooms.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        )}

        {/* Classrooms Grid */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
          </div>
        ) : classrooms.length === 0 ? (
          <div className="text-center py-12">
            <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
            <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white">No classrooms yet</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {user?.user_metadata?.role === 'admin'
                ? 'Create your first classroom to get started'
                : 'Your classrooms will appear here once you are enrolled'}
            </p>
          </div>
        ) : filteredClassrooms.length === 0 ? (
          <div className="text-center py-12">
            <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
            <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white">No classrooms found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Try adjusting your search query
            </p>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto pr-2 pb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-4">
              {filteredClassrooms.map((classroom) => (
                <div
                  key={classroom.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-200 cursor-pointer border border-transparent hover:border-primary-100 dark:border-gray-700 dark:hover:border-primary-800 group"
                  onClick={() => joinClassroom(classroom.id)}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="bg-primary-100 dark:bg-primary-900 p-3 rounded-lg group-hover:bg-primary-200 dark:group-hover:bg-primary-800 group-hover:scale-110 transition-all duration-200">
                        <BookOpenIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                        {classroom.tutor_id === user?.id ? 'Your Class' : 'Enrolled'}
                      </span>
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {classroom.name}
                    </h3>

                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                      {classroom.description}
                    </p>

                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center">
                        <UserGroupIcon className="h-4 w-4 mr-1" />
                        <span>{classroom.student_count || 0} students</span>
                      </div>
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        <span>Active</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create Classroom Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fade-in">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 animate-fade-in-up">
              <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Create New Classroom</h2>

              <form onSubmit={createClassroom}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Classroom Name
                  </label>
                  <input
                    type="text"
                    value={newClassroom.name}
                    onChange={(e) => setNewClassroom({ ...newClassroom, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., Math 101"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newClassroom.description}
                    onChange={(e) => setNewClassroom({ ...newClassroom, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    rows="3"
                    placeholder="What will students learn in this classroom?"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 dark:bg-primary-700 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        </div>

        {/* Completed Sessions Sidebar - Hidden for admins */}
        {user?.user_metadata?.role !== 'admin' && (
          <div className="w-full lg:w-80 flex-shrink-0 pb-6 lg:pb-0">
            <div className="lg:sticky lg:top-0">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-transparent dark:border-gray-700">
                <div className="bg-primary-600 dark:bg-primary-700 text-white px-6 py-4">
                  <h2 className="text-lg font-semibold flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Completed Sessions
                  </h2>
                  <p className="text-xs text-primary-100 dark:text-primary-200 mt-1">Last 30 days</p>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {completedLoading ? (
                    <div className="flex justify-center items-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
                    </div>
                  ) : completedSessions.length === 0 ? (
                    <div className="text-center py-8 px-4">
                      <BookOpenIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No completed sessions yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {completedSessions.map((session) => {
                        const classroom = classrooms.find(c => c.id === session.classroom_id);
                        const userRole = session.userRole;
                        const isConfirmedByUser = userRole === 'tutor'
                          ? session.tutor_confirmed
                          : (session.student_confirmations || []).includes(user.id);
                        const isFullyConfirmed = session.completion_status === 'confirmed';

                        return (
                          <div key={session.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h3 className="font-medium text-gray-900 dark:text-white text-sm">{session.title}</h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {classroom?.name || 'Unknown Classroom'}
                                </p>
                              </div>
                              {isFullyConfirmed ? (
                                <CheckCircleIcon className="h-5 w-5 text-green-500 dark:text-green-400 flex-shrink-0" title="Fully confirmed" />
                              ) : isConfirmedByUser ? (
                                <CheckCircleIcon className="h-5 w-5 text-yellow-500 dark:text-yellow-400 flex-shrink-0" title="Awaiting confirmations" />
                              ) : (
                                <XCircleIcon className="h-5 w-5 text-gray-300 dark:text-gray-600 flex-shrink-0" title="Not confirmed" />
                              )}
                            </div>

                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                              {moment(session.end_time).format('MMM D, h:mm A')}
                            </div>

                            {!isConfirmedByUser && (
                              <button
                                onClick={() => handleConfirmSession(session.id)}
                                className="w-full mt-2 px-3 py-1.5 bg-primary-600 dark:bg-primary-700 text-white text-xs font-medium rounded hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
                              >
                                Confirm Attendance
                              </button>
                            )}

                            {isConfirmedByUser && (
                              <div className="mt-2 text-xs">
                                {isFullyConfirmed ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
                                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                                    Fully Confirmed
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300">
                                    <ClockIcon className="h-3 w-3 mr-1" />
                                    Awaiting {userRole === 'tutor' ? 'student' : 'tutor'} confirmation
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Too Early Modal */}
      {showTooEarlyModal && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 w-full max-w-md mx-4 text-center">
            <div className="mb-4">
              <ClockIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Hold Your Horses! 🐴
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                This session hasn't started yet. You can join the classroom 5 minutes before the session begins.
              </p>
              <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg mb-6">
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                  {selectedSession.title}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Starts: {moment(selectedSession.start_time).format('MMMM D, YYYY [at] h:mm A')}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  ({moment(selectedSession.start_time).fromNow()})
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowTooEarlyModal(false);
                setSelectedSession(null);
              }}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-semibold"
            >
              Got It!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
